import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { crewClaimContext } from '~/app/features/crew/crewClaimMiddleware';
import { crewOrderContext } from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import Payments from '~/stores/payments';
import api from '~/utils/api';
import {
  isCreditClaim,
  pendingClaimLineItemToCalculatedLineItem,
  pendingClaimsToClaimLineItemCreate,
} from '~/utils/claimLineItem';
import { ClaimType, ExchangeMethod } from '~/utils/constants';
import { getDeviceFingerprint } from '~/utils/fingerprint';
import { logMessage } from '~/utils/observability';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { creditResolutionMethodSchema } from '~/utils/schemas';
import { AsyncReturnType } from '~/utils/types';
import { getCreditMethods } from './returnSummaryCreditMethod';
import { getExchangeMethods } from './returnSummaryExchangeMethod';
import { getReturnSummaryParams } from './returnSummarySearchParams';

// ----------- Refund Methods -----------

const getOutstandingBalance = (
  authorizedAmount: number,
  refundMethod: z.infer<typeof creditResolutionMethodSchema> | null,
) => {
  // If the balance is positive, this indicates in favor of the merchant
  if (authorizedAmount > 0) {
    return {
      kind: 'Payment' as const,
      total: authorizedAmount,
      label: 'Payment due',
    };
  }

  if (authorizedAmount === 0 || !refundMethod) return null;

  return {
    total: authorizedAmount,
    kind: refundMethod,
    label: refundMethod === 'Refund' ? 'Refund owed' : 'Gift Card owed',
  };
};

const addCreditMethodsBadgeContent = (
  creditMethodsOffered: ReturnType<typeof getCreditMethods>['offered'],
  returnSummary: AsyncReturnType<typeof api.calculateBeforeClaimSubmission>,
) => {
  const incentiveSection = returnSummary.credits.sections.find(
    (section) => section.type === 'INCENTIVE',
  );

  return match(incentiveSection)
    .with({ amount: P.number.positive() }, (section) =>
      creditMethodsOffered.map((method) =>
        method.type === 'Gift_Card'
          ? {
              ...method,
              amount: section.amount,
            }
          : method,
      ),
    )
    .otherwise(() => creditMethodsOffered);
};

export const { loader, useLoaderData } = makeLoader(
  async ({ params: { store, idFromPlatform }, context, request }, appStore) => {
    const crewClaim = context.get(crewClaimContext);
    const settings = context.get(settingsContext);
    const order = context.get(crewOrderContext);
    if (!crewClaim.returnMethod || crewClaim.status !== 'new') {
      logMessage('Return Summary: Missing return method or invalid status', {
        extra: {
          returnMethod: crewClaim.returnMethod,
          status: crewClaim.status,
        },
      });

      return redirect(`/${store}/order/${idFromPlatform}`);
    }

    const params = getReturnSummaryParams(request.url);

    // always clear out fees, as they may be re-assigned/recalculated; gift card is defaulted, which may differ from the last selection
    crewClaim.claimLineItems.forEach((cli) => {
      appStore.dispatch(
        appStore.actions.crewClaim.updateClaimLineItem({
          id: cli.id,
          updates: {
            fees: [],
          },
        }),
      );
    });

    const creditMethod = getCreditMethods(
      settings,
      crewClaim.claimLineItems,
      params.creditMethod,
    );

    const exchangeMethod = getExchangeMethods(
      { crewClaim, order, settings },
      params.exchangeMethod,
    );

    // ! It is expected at the API layer, than the claim line items will have the
    // ! creditMethod applied... should we apply it to the draft claim line item or keep intercepting as we are today
    const fromCustomerItems = crewClaim.claimLineItems;

    const returnSummary = await api.calculateBeforeClaimSubmission({
      params: { storefrontId: settings.storefrontId },
      body: {
        id: order.id,
        claimType: ClaimType.return,
        calculatedLineItems: fromCustomerItems.map((cli) =>
          isCreditClaim(cli)
            ? pendingClaimLineItemToCalculatedLineItem({
                ...cli,
                requestedResolutionMethodEnum:
                  creditMethod.selected?.type ??
                  cli.requestedResolutionMethodEnum,
              })
            : pendingClaimLineItemToCalculatedLineItem(cli),
        ),
        exchangeMethod: exchangeMethod.selected?.type,
        returnShipment: crewClaim.returnMethod,
        toCustomerRate: crewClaim.toCustomerRate,
        creditResolutionMethod: creditMethod.selected?.type ?? undefined,
        shopNowIncentiveAmountApplied: crewClaim.shopNowIncentiveAmountApplied,
      },
    });

    returnSummary.lineItemApplications.forEach(({ id, ...updates }) => {
      appStore.dispatch(
        appStore.actions.crewClaim.updateClaimLineItem({
          id,
          updates,
        }),
      );
    });

    returnSummary.lineItemFeeApplications.forEach(
      ({ calculatedLineItemId, storeRuleId, fee }) => {
        appStore.dispatch(
          appStore.actions.crewClaim.updateClaimLineItem({
            id: calculatedLineItemId,
            updates: {
              fees: [
                {
                  displayName: fee.displayName,
                  amount: fee.amount,
                  storeRuleId,
                },
              ],
            },
          }),
        );
      },
    );

    const { balance, paymentIntent } = returnSummary;

    // TODO: it may make sense to include the payment intent in the balance object.. instead of separately
    // the max amount that can be can be charge to the customer
    const authorizedAmount = paymentIntent ? paymentIntent.amount : balance;

    const outstandingBalance = getOutstandingBalance(
      authorizedAmount,
      creditMethod.selected?.type ?? null,
    );

    if (paymentIntent && outstandingBalance?.kind === 'Payment') {
      Payments.initializePayment(
        paymentIntent.clientSecret,
        paymentIntent.idFromPlatform,
      );
    } else {
      Payments.reset();
    }

    creditMethod.offered = addCreditMethodsBadgeContent(
      creditMethod.offered,
      returnSummary,
    );

    // a credit lineage could not trace is still at $0, so it is only an estimate until review
    const isOriginatingOrderCredit =
      settings.return.isClaimLineageEnabled &&
      !!order.isExchangeOfExchange &&
      crewClaim.claimLineItems.some(
        (cli) =>
          isCreditClaim(cli) &&
          (cli.lineItem.exchangeValueTrace === 'ambiguous' ||
            cli.lineItem.exchangeValueTrace === 'unresolved'),
      );

    return {
      isOriginatingOrderCredit,
      timeToReturnBeforeCharging:
        order.offerInstantExchange?.timeToReturnBeforeCharging ?? 7,
      credits: returnSummary.credits,
      deductions: returnSummary.deductions,
      exchangeMethod,
      balance,
      creditMethod,
      authorizedAmount,
      outstandingBalance,
    };
  },
);

export enum FormKey {
  claimValues = 'claimValues',
  exchangeMethod = 'exchangeMethod',
  creditMethod = 'creditMethod',
  paymentRequired = 'paymentRequired',
}

const coerceAbsoluteNumber = z.coerce.number().transform(Math.abs);

const claimValues = z.object({
  feeName: z.string().optional(),
  incentive: coerceAbsoluteNumber,
  toCustomerShipping: coerceAbsoluteNumber,
  fromCustomerShipping: coerceAbsoluteNumber,
  fee: coerceAbsoluteNumber,
  balance: coerceAbsoluteNumber,
  authorizedAmount: coerceAbsoluteNumber,
});

export type ClaimValues = z.infer<typeof claimValues>;

const formDataSchema = zfd.formData({
  [FormKey.claimValues]: zfd.json(claimValues),
  [FormKey.exchangeMethod]: zfd.text(z.enum(ExchangeMethod).optional()),
  [FormKey.creditMethod]: zfd.text(creditResolutionMethodSchema.optional()),
  [FormKey.paymentRequired]: zfd.checkbox().optional(),
});

export const { action, useActionResult } = makeAction(
  async ({ context, formData, params: { store } }, appStore) => {
    const crewClaim = context.get(crewClaimContext);
    const order = context.get(crewOrderContext);
    const { storefrontId, stripeConfig } = context.get(settingsContext);
    const { returnMethod } = crewClaim;

    if (!crewClaim.customerInfo) {
      throw new Error('Context Error: Missing customer information');
    }

    if (!returnMethod) {
      throw new Error('Context Error: Missing From Customer return method');
    }

    const formDataResult = formDataSchema.safeParse(formData);

    if (!formDataResult.success) {
      console.error('Form Data Parsing Error', formDataResult.error);
      throw new Error('Malformed form data', {
        cause: {
          error: formDataResult.error,
          entries: Array.from(Object.entries(formData)),
        },
      });
    }

    const {
      claimValues: {
        incentive: giftCardIncentiveAmountApplied,
        toCustomerShipping: exchangeOrderShippingAmountApplied,
        fromCustomerShipping: returnShippingAmountApplied,
        fee,
        feeName,
        balance,
        authorizedAmount,
      },
      exchangeMethod,
      paymentRequired,
    } = formDataResult.data;

    const { id } = order;
    const { email: customerEmail } = crewClaim.customerInfo;
    const address = {
      ...crewClaim.customerInfo.address,
      firstName: crewClaim.customerInfo.firstName,
      lastName: crewClaim.customerInfo.lastName,
    };

    const claimLineItems = pendingClaimsToClaimLineItemCreate(
      crewClaim.claimLineItems,
    );

    //* Submission Starts Here *//
    const fingerprint = await getDeviceFingerprint(customerEmail);
    //! lock the claim to prevent double submissions
    appStore.dispatch(appStore.actions.crewClaim.submitClaim());
    const confirmation = paymentRequired
      ? Payments.confirmPayment()
      : Promise.resolve(null);

    return confirmation
      .then((paymentIntentId) =>
        api.createCrewClaim({
          params: { storefrontId },
          body: {
            claimLineItems,
            claimType: ClaimType.return,
            originalStoreOrderId: id,
            fingerprint,
            customerEmail,
            shopNowId: crewClaim.shopNowId,
            billingAddress: address,
            shippingAddress: address,
            giftCardIncentiveAmountApplied,
            exchangeOrderShippingAmountApplied,
            feeApplied: fee,
            feeName,
            isGiftReturn: !!order.isGiftReturn,
            returnShippingAmountApplied,
            shipment: returnMethod,
            source: 'Customer_App',
            shopNowIncentiveAmountApplied:
              crewClaim.shopNowIncentiveAmountApplied,

            // logically if shouldConfirmPayment is true, then paymentIntentId and stripeConfig should exist, but TS complains.
            ...(stripeConfig &&
              paymentIntentId &&
              paymentRequired && {
                customerPayment: {
                  idFromPlatform: paymentIntentId,
                  currencyCode: order.currencyCode,
                  stripeConfig,
                  // ! amount = non-refundable portion of the balance (IE, fees, shipping, etc.)
                  //! balance should be in dollars or the ratio equivalent of the currency
                  amount: balance,
                  // ! authorizedAmount = refundable portion of the balance (IE the instant exchange amount ) OR undefined if it does not exist
                  // ! We will know to capture immediately or wait based on the existence of authorizedAmount
                  authorizedAmount:
                    exchangeMethod === ExchangeMethod.instant
                      ? authorizedAmount
                      : undefined,
                },
              }),
          },
        }),
      )
      .then((claim) => {
        appStore.dispatch(appStore.actions.completeCrewClaim());

        return redirect(`/${store}/claim/overview/${claim.externalId}`);
      })
      .catch((err) => {
        appStore.dispatch(appStore.actions.crewClaim.unlockClaim());

        return {
          ok: false,
          message: err instanceof Error ? err.message : String(err),
        };
      });
  },
);
